import React, { useCallback, useMemo } from 'react';
import { Field } from '@groupelacentrale/contact-storybook';
import { FormikErrors, FormikTouched } from 'formik';
import { VehicleConditionOptions, VehicleVersionStateFields } from 'types';
import OneToMany from 'components/OneToMany/OneToMany';
import { VehicleConditionModal } from './VehicleConditionModal';
import { vehicleConditionMapping } from 'utils/vehicleCondition';

interface Props {
  refs?: Record<keyof VehicleVersionStateFields, React.RefObject<HTMLDivElement>>;
  updatedFields?: Record<keyof VehicleVersionStateFields, boolean>;
  values: VehicleVersionStateFields;
  touched: FormikTouched<VehicleVersionStateFields>;
  errors: FormikErrors<VehicleVersionStateFields>;
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean | undefined) => void;
  handleBlur?: (e: React.FocusEvent<unknown>) => void;
  isPro?: boolean;
}

const REF_PRO_REGEX = new RegExp(/^[0-9a-zA-Z-_]+$/);

export const VehicleStateFormView: React.FC<Props> = ({
  values,
  refs,
  updatedFields,
  touched,
  errors,
  setFieldValue,
  handleBlur,
  isPro = false,
}) => {
  const onImportedChange = useCallback((selectedValue: string) => setFieldValue('imported', selectedValue === 'Oui'), [
    setFieldValue,
  ]);

  const onFirstHandChange = useCallback(
    (selectedValue: string) => setFieldValue('firstHand', selectedValue === 'Oui'),
    [setFieldValue],
  );

  const onMileageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.length > 0 ? Number.parseInt(e.target.value.replace(/\D/, '')) : undefined;

      if (value === undefined || !isNaN(value)) {
        setFieldValue('mileage', value || '');
      }
    },
    [setFieldValue],
  );

  const onReferenceProChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.length > 0 ? e.target.value : undefined;
      setFieldValue('referencePro', value);
    },
    [setFieldValue],
  );

  const referenceProErrorMessage = useMemo(
    () =>
      values.referencePro && !REF_PRO_REGEX.test(values.referencePro)
        ? 'Votre référence est incorrecte. Les seuls caractères autorisés sont les chiffres, lettres, _ et -'
        : touched.referencePro
        ? errors.referencePro
        : undefined,
    [values.referencePro, touched.referencePro, errors.referencePro],
  );

  const onConditionChange = useCallback(
    (selectedValue: string) => {
      setFieldValue('condition', selectedValue);
    },
    [setFieldValue],
  );

  return (
    <div className="vehicle-version-data-fields">
      <OneToMany
        ref={refs?.imported}
        name="imported"
        label={<span className={updatedFields?.imported ? 'updated-field' : undefined}>Véhicule d&#039;import*</span>}
        value={values.imported !== undefined ? (values.imported ? 'Oui' : 'Non') : undefined}
        options={['Oui', 'Non']}
        onSelect={onImportedChange}
        errorMessage={touched.imported ? errors.imported : undefined}
      />
      <OneToMany
        ref={refs?.firstHand}
        name="firstHand"
        label={<span className={updatedFields?.firstHand ? 'updated-field' : undefined}>Première main*</span>}
        value={values.firstHand !== undefined ? (values.firstHand ? 'Oui' : 'Non') : undefined}
        options={['Oui', 'Non']}
        onSelect={onFirstHandChange}
        errorMessage={touched.firstHand ? errors.firstHand : undefined}
      />
      <Field
        ref={refs?.mileage}
        name="mileage"
        label={<span className={updatedFields?.mileage ? 'updated-field' : undefined}>Kilométrage*</span>}
        value={values.mileage ?? ''}
        unit="km"
        onChange={onMileageChange}
        onBlur={handleBlur}
        errorMessage={touched.mileage ? errors.mileage : undefined}
      />
      {isPro && (
        <Field
          ref={refs?.referencePro}
          name="referencePro"
          label="Référence Pro"
          value={values.referencePro ?? ''}
          onChange={onReferenceProChange}
          errorMessage={referenceProErrorMessage}
        />
      )}
      <OneToMany
        ref={refs?.condition}
        name="condition"
        label={<span className={updatedFields?.condition ? 'updated-field' : undefined}>État du véhicule*</span>}
        asideComponent={<VehicleConditionModal />}
        value={values.condition}
        options={Object.values(VehicleConditionOptions)}
        onSelect={onConditionChange}
        translatedOptions={vehicleConditionMapping}
        errorMessage={touched.condition ? errors.condition : undefined}
      />
    </div>
  );
};
